import { supabase } from '@/integrations/supabase/client';
import type { SimplifiedBioAnalysis } from '@/types/bio-analysis';

/**
 * Lovable AI Bio Evaluator - v3 (Topic-Focused)
 * 
 * Versão simplificada focada em extração máxima de tópicos:
 * - 50-100+ tópicos extraídos (vs 30-60 anterior)
 * - < 2s com Lovable AI (google/gemini-2.0-flash-exp)
 * - Matching trivial por Jaccard Similarity
 * - Cache de 10 minutos
 * - 90% menos complexidade
 */
class LovableAIBioEvaluator {
  private cache = new Map<string, SimplifiedBioAnalysis>();
  private cacheExpiry = new Map<string, number>();
  private readonly CACHE_DURATION = 10 * 60 * 1000; // 10 minutos

  /**
   * Avalia uma bio usando Lovable AI com foco em tópicos máximos
   */
  async evaluateBio(bio: string): Promise<SimplifiedBioAnalysis> {
    const cacheKey = this.hashBio(bio);
    
    // Verificar cache primeiro
    if (this.cache.has(cacheKey)) {
      const expiryTime = this.cacheExpiry.get(cacheKey) || 0;
      if (Date.now() < expiryTime) {
        console.log('✅ Using cached enhanced bio analysis (10min cache)');
        return this.cache.get(cacheKey)!;
      } else {
        // Cache expirado
        this.cache.delete(cacheKey);
        this.cacheExpiry.delete(cacheKey);
        console.log('⏱️ Cache expired, re-analyzing with Lovable AI');
      }
    }

    // Validação básica de comprimento
    if (!bio || bio.trim().length < 10) {
      const result = this.createMinimalAnalysis(
        'Sua bio está muito curta. Escreva pelo menos 10 caracteres para análise.',
        0,
        'local_length'
      );
      return result;
    }

    try {
      console.log('🚀 Calling evaluate-bio-v2 edge function (Lovable AI)...');
      const startTime = Date.now();
      
      const { data, error } = await supabase.functions.invoke('evaluate-bio-v2', {
        body: { bio }
      });

      if (error) {
        console.error('Edge function error:', error);
        throw error;
      }

      const processingTime = Date.now() - startTime;
      console.log(`✅ Enhanced analysis received in ${processingTime}ms - ${data.extractedDataPoints} data points`);

      // Cache o resultado
      this.cache.set(cacheKey, data);
      this.cacheExpiry.set(cacheKey, Date.now() + this.CACHE_DURATION);
      console.log('💾 Enhanced analysis cached for 10 minutes');

      return data;

    } catch (error) {
      console.error('Error in Lovable AI bio evaluation:', error);
      
      // Fallback: análise básica local
      return this.createFallbackAnalysis(bio, error);
    }
  }

  /**
   * Gera hash da bio para cache (normalizado)
   */
  private hashBio(bio: string): string {
    return bio.trim().toLowerCase().replace(/\s+/g, ' ');
  }

  /**
   * Cria análise mínima para bios muito curtas
   */
  private createMinimalAnalysis(
    feedback: string,
    score: number,
    source: 'local_length' | 'local_regex'
  ): SimplifiedBioAnalysis {
    return {
      isWellWritten: false,
      score,
      feedback,
      topics: [],
      validationSource: source,
      extractedTopicsCount: 0
    };
  }

  /**
   * Cria análise de fallback quando AI falha
   */
  private createFallbackAnalysis(bio: string, error: any): SimplifiedBioAnalysis {
    console.warn('Using fallback analysis due to error:', error);
    
    // Análise básica usando regex
    const topics = this.extractBasicTopics(bio);
    const score = Math.min(70, Math.max(30, topics.length * 5));
    
    return {
      isWellWritten: bio.length > 50,
      score,
      feedback: 'Análise básica disponível. A análise completa com IA está temporariamente indisponível.',
      topics,
      validationSource: 'fallback',
      extractedTopicsCount: topics.length,
      processingTime: 0
    };
  }

  /**
   * Extrai tópicos básicos usando regex (fallback)
   */
  private extractBasicTopics(bio: string): string[] {
    const bioLower = bio.toLowerCase();
    const topics: string[] = [];
    
    // Keywords básicas para detecção
    const keywords = [
      'viagem', 'viagens', 'viajar',
      'musica', 'jazz', 'rock', 'mpb',
      'esporte', 'academia', 'yoga', 'pilates',
      'gastronomia', 'restaurante', 'culinaria',
      'praia', 'mar', 'litoral',
      'cinema', 'filme', 'serie',
      'leitura', 'livro', 'ler',
      'arte', 'pintura', 'fotografia',
      'tecnologia', 'startup', 'inovacao',
      'vinho', 'cafe', 'cerveja',
      'dança', 'dancar',
      'natureza', 'montanha', 'trilha'
    ];
    
    keywords.forEach(keyword => {
      if (bioLower.includes(keyword)) {
        topics.push(keyword);
      }
    });
    
    return [...new Set(topics)]; // Remove duplicatas
  }

  /**
   * Limpa todo o cache
   */
  clearCache(): void {
    this.cache.clear();
    this.cacheExpiry.clear();
    console.log('🗑️ Enhanced bio analysis cache cleared');
  }

  /**
   * Retorna estatísticas do cache
   */
  getCacheStats() {
    return {
      size: this.cache.size,
      entries: Array.from(this.cache.keys()).map(key => ({
        key: key.substring(0, 50) + '...',
        expiresIn: Math.max(0, (this.cacheExpiry.get(key) || 0) - Date.now())
      }))
    };
  }
}

// Singleton instance
export const lovableAIBioEvaluator = new LovableAIBioEvaluator();

// Export convenience function
export const evaluateBioWithLovableAI = (bio: string): Promise<SimplifiedBioAnalysis> => {
  return lovableAIBioEvaluator.evaluateBio(bio);
};
